import { createBattle, forfeitBattle, reduceBattle } from 'features/battle/engine';
import type { BattleInput, BattleState, PlayerId } from 'features/battle/types';

export const BATTLE_ROOM_ID_RE = /^[A-HJ-NP-Z2-9]{4}$/;
export const BATTLE_ROOM_ID_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export type BattleSeat = {
  id: string;
  name: string;
  deck: string[];
  /** True when every badge in `deck` was claimed. */
  isPure?: boolean;
};

export type BattleAbandon = {
  id: string;
  name: string;
};

export type BattleRoom = {
  id: string;
  seed: number;
  seq: number;
  host: BattleSeat;
  guest: BattleSeat | null;
  /** This battle only allows pure decks (every badge claimed). */
  pureOnlyBattle?: boolean;
  state: BattleState | null;
  abandonedBy?: BattleAbandon | null;
  /** The last few inputs, by the `seq` each produced, so a client that missed several can replay them. */
  trail?: BattleTrailStep[];
};

export type BattleTrailStep = { seq: number; input: BattleInput };

export const BATTLE_TRAIL_LENGTH = 16;

/**
 * States between `from` and `to`, rebuilt from `to`'s trail, ending on `to.state`.
 * Empty when there is nothing to step through or the replay would not land exactly on `to`.
 */
export const replayTrail = (from: BattleRoom | null, to: BattleRoom): BattleState[] => {
  if (!from?.state || !to.state || to.seq - from.seq < 2) return [];
  const steps = (to.trail ?? []).filter((step) => step.seq > from.seq && step.seq <= to.seq);
  if (steps.length !== to.seq - from.seq || steps.some((step, index) => step.seq !== from.seq + index + 1)) return [];
  const states: BattleState[] = [];
  let state = from.state;
  for (const step of steps) {
    const next = reduceBattle(state, step.input);
    if (next === state) return [];
    states.push(next);
    state = next;
  }
  if (JSON.stringify(state) !== JSON.stringify(to.state)) return [];
  states[states.length - 1] = to.state;
  return states;
};

export const normalizeBattleRoomId = (raw: string): string | null => {
  const id = raw.trim().toUpperCase();
  return BATTLE_ROOM_ID_RE.test(id) ? id : null;
};

export const parseBattleRoomId = (pathname: string): string | null => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'battle' || !parts[1]) return null;
  return normalizeBattleRoomId(parts[1]);
};

export const battleRoomPath = (roomId: string): string => `/battle/${roomId}`;

export const newBattleRoomId = (): string => {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => BATTLE_ROOM_ID_ALPHABET[byte % BATTLE_ROOM_ID_ALPHABET.length]).join('');
};

export type EmptyHostRoom = {
  id: string;
  playerId: string;
  name: string;
  deck: string[];
  /** The host's queued deck is built only from claimed badges. */
  hostDeckPure: boolean;
  /** This battle only allows pure decks. */
  pureOnlyBattle: boolean;
};

export const emptyHostRoom = ({
  id,
  playerId,
  name,
  deck,
  hostDeckPure,
  pureOnlyBattle,
}: EmptyHostRoom): BattleRoom => ({
  id,
  seed: Date.now() % 1_000_000 || 1,
  seq: 0,
  host: { id: playerId, name, deck: [...deck], ...(hostDeckPure ? { isPure: true } : {}) },
  guest: null,
  ...(pureOnlyBattle ? { pureOnlyBattle: true } : {}),
  state: null,
  abandonedBy: null,
});

export const isAbandoned = (room: BattleRoom): boolean => Boolean(room.abandonedBy);

/** Backgrounding the tab should only leave a live match, not a waiting lobby. */
export const roomNeedsLeaveOnPageHide = (room: BattleRoom | null): boolean =>
  Boolean(room && room.state && room.state.phase !== 'game_over' && !room.abandonedBy);

/** A write that does not move seq forward must not replace the stored room. */
export const battleRoomWriteIsStale = (existingSeq: number | null, incomingSeq: number): boolean =>
  existingSeq != null && incomingSeq <= existingSeq;

export const withAbandonedBy = (room: BattleRoom, playerId: string, name: string): BattleRoom | null => {
  if (room.abandonedBy) return null;
  if (room.host.id !== playerId && room.guest?.id !== playerId) return null;
  return {
    ...room,
    seq: room.seq + 1,
    abandonedBy: { id: playerId, name },
  };
};

export const seatForPlayer = (room: BattleRoom, playerId: string): PlayerId | null => {
  if (room.host.id === playerId) return 0;
  if (room.guest?.id === playerId) return 1;
  return null;
};

/** Live match leave is a concede so the other seat sees the win modal, same as a CPU fold. */
export const concedeLiveRoom = (room: BattleRoom, playerId: string): BattleRoom | null => {
  if (!room.state || room.state.phase === 'game_over' || room.abandonedBy) return null;
  const loser = seatForPlayer(room, playerId);
  if (loser == null) return null;
  return {
    ...room,
    seq: room.seq + 1,
    state: forfeitBattle(room.state, loser),
  };
};

export const startPvpBattle = (room: BattleRoom): BattleState => {
  if (!room.guest) throw new Error('Cannot start a battle without a guest.');
  return createBattle({
    decks: [room.host.deck, room.guest.deck],
    names: [room.host.name, room.guest.name],
    seed: room.seed,
    cpu: null,
    coinToss: true,
    pure: [Boolean(room.host.isPure), Boolean(room.guest.isPure)],
  });
};

export const ensureBattle = (room: BattleRoom): BattleRoom => {
  if (room.abandonedBy || room.state || !room.guest) return room;
  return { ...room, state: startPvpBattle(room) };
};
